import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ShieldCheck, Users, Settings, Star, ExternalLink, X } from 'lucide-react'
import { SITE } from '../constants/site'
import { useInViewCount } from '../lib/useInViewCount'
import { useEscapeKey } from '../lib/useEscapeKey'
import { getGoogleRating } from '../api/google'

/* Poussières en suspension (positions déterministes) */
const DUST = Array.from({ length: 14 }, (_, i) => ({
  left: (i * 67) % 100,
  size: 1.5 + ((i * 7) % 4),
  dur: 12 + ((i * 5) % 11),
  delay: -(i * 1.7) % 14,
  op: 0.25 + ((i % 5) * 0.1),
  drift: (i % 2 === 0 ? 1 : -1) * (8 + (i % 5) * 6),
}))

export function Hero() {
  const [googleModal, setGoogleModal] = useState(false)
  useEscapeKey(() => setGoogleModal(false))

  const { data: google } = useQuery({ queryKey: ['google-rating'], queryFn: getGoogleRating })
  const googleNote = google?.rating ?? 4.7
  const googleTotal = google?.total ?? 248

  return (
    <section
      id="accueil"
      className="relative flex min-h-[92vh] flex-col overflow-hidden bg-night"
    >
      {/* ░░ Décor showroom (Ken Burns) ░░ */}
      <div className="absolute inset-0">
        <img
          src="/img/hero-fond2.jpg"
          alt=""
          fetchPriority="high"
          className="ken-burns h-full w-full scale-105 object-cover object-center"
        />
      </div>

      {/* ░░ Voile gauche (desktop) : lisibilité + masque le nettoyage ░░ */}
      <div
        className="pointer-events-none absolute inset-0 hidden sm:block"
        style={{
          background:
            'linear-gradient(90deg, rgba(7,24,43,0.72) 0%, rgba(7,24,43,0.62) 24%, rgba(7,24,43,0.28) 44%, rgba(7,24,43,0) 62%)',
        }}
      />
      {/* ░░ Voile mobile : assombrit pour le texte pleine largeur ░░ */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-night via-night/85 to-night/35 sm:hidden" />
      {/* ░░ Renfort bas (ancrage du panneau de stats) ░░ */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-night via-night/55 to-transparent sm:h-2/5 sm:via-night/40" />

      {/* ░░ Poussières (premier plan) ░░ */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {DUST.map((d, i) => (
          <span
            key={i}
            className="dust absolute bottom-[12%] rounded-full bg-orange/70"
            style={{
              left: `${d.left}%`,
              width: d.size,
              height: d.size,
              ['--dust-dur' as string]: `${d.dur}s`,
              ['--dust-delay' as string]: `${d.delay}s`,
              ['--dust-op' as string]: d.op,
              ['--dust-x' as string]: `${d.drift}px`,
            }}
          />
        ))}
      </div>

      {/* ░░ Vignette ░░ */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(120% 100% at 50% 38%, transparent 64%, rgba(0,0,0,0.3) 100%)' }}
      />

      {/* ░░ Mascotte à droite, alignée sur la largeur du contenu (comme le menu) ░░ */}
      <div className="pointer-events-none absolute inset-y-0 left-1/2 z-[1] hidden w-full max-w-7xl -translate-x-1/2 px-5 sm:block lg:px-8">
        <img
          src="/img/hero-bonhomme.png"
          alt="Maison Gilles — votre conseiller en matériel de jardin Honda et Husqvarna"
          className="absolute bottom-0 right-5 h-[74%] w-auto object-contain object-bottom drop-shadow-[0_24px_50px_rgba(0,0,0,0.45)] sm:h-[84%] lg:right-8 lg:h-[92%]"
        />
      </div>

      {/* ════════ CONTENU ════════ */}
      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 py-12 lg:px-8">
        <div className="max-w-lg">
          <h1
            className="animate-fade-up font-display text-[2.7rem] font-extrabold leading-[1.05] text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)] sm:text-6xl"
            style={{ animationDelay: '0.08s' }}
          >
            Votre spécialiste en matériel de jardin à{' '}
            <span className="relative inline-block text-orange">
              Jambes
              <span className="absolute -bottom-1 left-0 h-1.5 w-full rounded-full bg-orange/80" />
            </span>
          </h1>
          <p
            className="animate-fade-up mt-7 max-w-xl text-base leading-relaxed text-white/90 [text-shadow:0_2px_12px_rgba(0,0,0,0.65)] sm:text-lg"
            style={{ animationDelay: '0.16s' }}
          >
            Une large gamme de produits Honda et Husqvarna. Vente, réparation et entretien assurés par
            une <span className="font-semibold text-orange">équipe passionnée</span>, au cœur d'une
            entreprise familiale depuis quatre générations.
          </p>
        </div>
      </div>

      {/* ════════ PANNEAU STATS (unifié, séparateurs) ════════ */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-10 lg:px-8">
        <div className="rise-in overflow-hidden rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-md">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            <Cell index={0}>
              <NumStat icon={<ShieldCheck strokeWidth={1.6} />} end={100} suffix="+" label="ans d'expertise" sub="Un savoir-faire transmis depuis 1925" />
            </Cell>
            <Cell index={1}>
              <NumStat icon={<Users strokeWidth={1.6} />} end={4} label="générations" sub="Une entreprise familiale de père en fils" />
            </Cell>
            <Cell index={2}>
              <AtelierStat />
            </Cell>
            <Cell index={3}>
              <button
                onClick={() => setGoogleModal(true)}
                title="Voir nos avis sur Google"
                className="block w-full rounded-lg text-left transition-opacity hover:opacity-80 focus-visible:outline-none"
              >
                <NumStat icon={<Star strokeWidth={1.6} />} end={googleNote} decimals suffix=" / 5" label="sur Google" sub={`${googleTotal} avis clients`} />
              </button>
            </Cell>
          </div>
        </div>
      </div>

      {/* Popup de redirection vers la fiche Google (avis) */}
      {googleModal && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-ink/50 p-4" onClick={() => setGoogleModal(false)}>
          <div role="dialog" aria-modal="true" aria-label="Redirection vers Google" className="w-full max-w-md rounded-2xl bg-white p-6 text-center text-ink shadow-soft" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end">
              <button onClick={() => setGoogleModal(false)} className="rounded-lg p-1.5 text-ink/50 hover:bg-sand" aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-orange/10 text-orange">
              <ExternalLink className="h-6 w-6" />
            </span>
            <h3 className="mt-4 font-display text-xl font-bold text-navy">Ouverture dans un nouvel onglet</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink/70">
              Notre fiche <strong className="text-navy">Google</strong> va s'ouvrir dans un nouvel onglet pour consulter nos avis. Vous ne quittez pas Maison Gilles.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setGoogleModal(false)}
                className="rounded-full border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-sand"
              >
                Annuler
              </button>
              <a
                href={SITE.googleMaps}
                target="_blank"
                rel="noreferrer"
                onClick={() => setGoogleModal(false)}
                className="rounded-full bg-orange px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-dk"
              >
                OK, ouvrir
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

/* ─────────── Cellule du panneau (gère les séparateurs responsive) ─────────── */
function Cell({ index, children }: { index: number; children: React.ReactNode }) {
  const borders = [
    index % 2 === 1 ? 'border-l border-white/10' : '',
    index >= 2 ? 'border-t border-white/10' : '',
    'lg:border-t-0',
    index === 0 ? 'lg:border-l-0' : 'lg:border-l lg:border-white/10',
  ].join(' ')
  return <div className={`p-4 sm:p-5 ${borders}`}>{children}</div>
}

/* ─────────── Cellule chiffrée (compteur animé au scroll) ─────────── */
function NumStat({
  icon,
  end,
  decimals,
  suffix,
  label,
  sub,
}: {
  icon: React.ReactNode
  end: number
  decimals?: boolean
  suffix?: string
  label: string
  sub: string
}) {
  const { ref, value } = useInViewCount(end)
  const display = decimals ? value.toFixed(1).replace('.', ',') : Math.round(value).toString()
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="flex items-center gap-3.5 sm:gap-4">
      <span className="shrink-0 text-orange [&_svg]:h-8 [&_svg]:w-8 sm:[&_svg]:h-9 sm:[&_svg]:w-9">{icon}</span>
      <div className="min-w-0">
        <p className="font-display text-2xl font-bold leading-none text-white sm:text-[1.7rem]">
          {display}
          {suffix && <span className="text-orange">{suffix}</span>}
        </p>
        <p className="mt-1.5 text-sm font-semibold leading-tight text-white/85">{label}</p>
        <p className="mt-0.5 hidden text-xs leading-snug text-white/45 sm:block">{sub}</p>
      </div>
    </div>
  )
}

/* ─────────── Cellule « Atelier agréé » (logos marques) ─────────── */
function AtelierStat() {
  return (
    <div className="flex items-center gap-3.5 sm:gap-4">
      <span className="shrink-0 text-orange [&_svg]:h-8 [&_svg]:w-8 sm:[&_svg]:h-9 sm:[&_svg]:w-9">
        <Settings strokeWidth={1.6} />
      </span>
      <div className="min-w-0">
        <p className="font-display text-xl font-bold leading-none text-white sm:text-[1.4rem]">Atelier agréé</p>
        <p className="mt-1.5 text-sm font-semibold leading-tight text-white/85">Entretien et réparation</p>
        <div className="mt-2 hidden items-center gap-3 opacity-80 sm:flex">
          <img src="/img/honda-logo.png" alt="Honda" className="h-3 w-auto" />
          <img src="/img/husqvarna-logo.png" alt="Husqvarna" className="h-3.5 w-auto" />
        </div>
      </div>
    </div>
  )
}
